// SelectDatasetModal.tsx
import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react'
import { Modal, Button, Form, Row, Col, Badge, Card, Dropdown } from 'react-bootstrap'
import {
  useGetContentMetadataQuery,
  useSampleContentQuery,
  useGetContentImageQuery,
} from '../../services/contentApi'
import { useAppSelector, useAppDispatch } from '../../store/hooks'
import { selectSelectedProjectId } from '../../store/slices/projectSlice'
import {
  selectDatasetSources,
  setDatasetSources,
  clearDatasetSources,
} from '../../store/slices/datasetSlice'
import { useGetProjectQuery } from '../../services/projectsApi'
import { useDatasetStats, type DatasetStats } from '../../utils/datasetStats'
import {
  Search,
  Database,
  Tag,
  Image,
  Layers,
  Check,
  Grid,
  List,
  Filter,
  ArrowUpDown,
  BarChart3,
  Package,
  X,
  CheckCircle2,
} from 'lucide-react'
import styled from 'styled-components'
import { isEqual } from 'lodash'

const StyledModal = styled(Modal)`
  .modal-dialog {
    max-width: 1200px;
  }

  .modal-content {
    background: linear-gradient(145deg, #1a202c 0%, #2d3748 100%);
    border: 1px solid rgba(102, 126, 234, 0.2);
    border-radius: 16px;
    box-shadow: 0 25px 50px rgba(0, 0, 0, 0.5), 0 0 0 1px rgba(255, 255, 255, 0.05);
    backdrop-filter: blur(10px);
    overflow: hidden;
  }

  .modal-header {
    background: transparent;
    border-bottom: 1px solid rgba(255, 255, 255, 0.1);
    padding: 1.5rem 2rem;
    border-radius: 16px 16px 0 0;

    .modal-title {
      color: #fff;
      font-weight: 600;
      font-size: 1.25rem;
      display: flex;
      align-items: center;
      gap: 0.75rem;
    }

    .btn-close {
      filter: invert(1) grayscale(100%) brightness(200%);
      opacity: 0.7;

      &:hover {
        opacity: 1;
      }
    }
  }

  .modal-body {
    background: transparent;
    padding: 0;
    display: flex;
    flex-direction: column;
    max-height: 75vh;
    overflow: hidden;
  }
`

const TopBar = styled.div`
  padding: 1.5rem 2rem;
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  flex-wrap: wrap;
  background: rgba(0, 0, 0, 0.2);
`

const SearchWrapper = styled.div`
  position: relative;
  flex: 1;
  min-width: 280px;
  max-width: 400px;

  .search-icon {
    position: absolute;
    left: 1rem;
    top: 50%;
    transform: translateY(-50%);
    color: rgba(255, 255, 255, 0.5);
    z-index: 1;
  }

  .form-control {
    padding-left: 2.75rem;
    height: 42px;
    background: rgba(0, 0, 0, 0.3);
    border: 1px solid rgba(102, 126, 234, 0.2);
    border-radius: 10px;
    color: #ffffff;
    font-size: 0.95rem;
    transition: all 0.2s;

    &:focus {
      background: rgba(0, 0, 0, 0.4);
      border-color: rgba(102, 126, 234, 0.5);
      box-shadow: 0 0 0 3px rgba(102, 126, 234, 0.15);
      color: #ffffff;
    }

    &::placeholder {
      color: rgba(255, 255, 255, 0.4);
    }
  }
`

const FilterGroup = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  flex-wrap: wrap;
`

const FilterButton = styled.button<{ $active: boolean }>`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.5rem 1rem;
  background: ${(props) =>
    props.$active ? 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' : 'rgba(0, 0, 0, 0.3)'};
  border: 1px solid
    ${(props) => (props.$active ? 'rgba(102, 126, 234, 0.5)' : 'rgba(102, 126, 234, 0.2)')};
  border-radius: 10px;
  color: ${(props) => (props.$active ? '#fff' : 'rgba(255, 255, 255, 0.7)')};
  font-size: 0.9rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;

  &:hover {
    background: ${(props) =>
      props.$active ? 'linear-gradient(135deg, #764ba2 0%, #667eea 100%)' : 'rgba(0, 0, 0, 0.4)'};
    border-color: rgba(102, 126, 234, 0.5);
    color: #fff;
  }

  svg {
    width: 14px;
    height: 14px;
  }
`

const ViewToggleGroup = styled.div`
  display: flex;
  gap: 0.5rem;
  background: rgba(0, 0, 0, 0.3);
  padding: 0.25rem;
  border-radius: 10px;
  border: 1px solid rgba(102, 126, 234, 0.2);
`

const ViewToggleButton = styled.button<{ $active: boolean }>`
  padding: 0.5rem 0.75rem;
  background: ${(props) =>
    props.$active ? 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' : 'transparent'};
  border: none;
  border-radius: 8px;
  color: ${(props) => (props.$active ? '#fff' : 'rgba(255, 255, 255, 0.6)')};
  cursor: pointer;
  transition: all 0.2s;
  display: flex;
  align-items: center;
  justify-content: center;

  &:hover {
    color: #fff;
    background: ${(props) =>
      props.$active
        ? 'linear-gradient(135deg, #764ba2 0%, #667eea 100%)'
        : 'rgba(102, 126, 234, 0.2)'};
  }

  svg {
    width: 16px;
    height: 16px;
  }
`

const SortDropdown = styled(Dropdown)`
  .dropdown-toggle {
    background: rgba(0, 0, 0, 0.3);
    border: 1px solid rgba(102, 126, 234, 0.2);
    border-radius: 10px;
    color: rgba(255, 255, 255, 0.7);
    padding: 0.5rem 1rem;
    font-size: 0.9rem;
    height: 42px;
    display: flex;
    align-items: center;
    gap: 0.5rem;

    &:hover,
    &:focus {
      background: rgba(0, 0, 0, 0.4);
      border-color: rgba(102, 126, 234, 0.5);
      color: #fff;
    }
  }

  .dropdown-menu {
    background: linear-gradient(145deg, #2d3748 0%, #1a202c 100%);
    border: 1px solid rgba(102, 126, 234, 0.2);
    border-radius: 10px;
    padding: 0.5rem;
    margin-top: 0.5rem;
    box-shadow: 0 10px 30px rgba(0, 0, 0, 0.4);
  }

  .dropdown-item {
    color: rgba(255, 255, 255, 0.7);
    border-radius: 6px;
    padding: 0.5rem 1rem;
    transition: all 0.2s;

    &:hover {
      background: rgba(102, 126, 234, 0.2);
      color: #fff;
    }

    &.active {
      background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
      color: #fff;
    }
  }
`

const ContentArea = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: 2rem;
  background: transparent;
`

const SelectionBanner = styled.div`
  background: linear-gradient(135deg, rgba(102, 126, 234, 0.15) 0%, rgba(118, 75, 162, 0.15) 100%);
  border: 1px solid rgba(102, 126, 234, 0.3);
  border-radius: 12px;
  padding: 1rem 1.5rem;
  margin-bottom: 1.5rem;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;

  .selection-info {
    display: flex;
    align-items: center;
    gap: 0.75rem;
    color: #fff;
    font-weight: 500;

    svg {
      color: rgba(102, 126, 234, 1);
    }
  }

  .clear-btn {
    background: transparent;
    border: 1px solid rgba(102, 126, 234, 0.3);
    color: rgba(255, 255, 255, 0.7);
    padding: 0.4rem 1rem;
    border-radius: 8px;
    font-size: 0.85rem;
    font-weight: 500;
    cursor: pointer;
    transition: all 0.2s;

    &:hover {
      background: rgba(102, 126, 234, 0.2);
      border-color: rgba(102, 126, 234, 0.5);
      color: #fff;
    }
  }
`

const ResultsHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1.5rem;

  .results-count {
    color: rgba(255, 255, 255, 0.6);
    font-size: 0.9rem;

    strong {
      color: #fff;
      font-weight: 600;
    }
  }

  .select-all-btn {
    background: transparent;
    border: 1px solid rgba(102, 126, 234, 0.2);
    color: rgba(255, 255, 255, 0.7);
    padding: 0.4rem 1rem;
    border-radius: 8px;
    font-size: 0.85rem;
    font-weight: 500;
    cursor: pointer;
    transition: all 0.2s;

    &:hover:not(:disabled) {
      background: rgba(102, 126, 234, 0.2);
      border-color: rgba(102, 126, 234, 0.5);
      color: #fff;
    }

    &:disabled {
      opacity: 0.4;
      cursor: not-allowed;
    }
  }
`

const CardsContainer = styled.div<{ $hasHover: boolean }>`
  transition: all 0.3s ease;

  .dataset-card-wrapper {
    transition: all 0.3s ease;
    ${(props) =>
      props.$hasHover &&
      `
      opacity: 0.4;
      filter: blur(1px);
      transform: scale(0.98);
    `}
  }

  .dataset-card-wrapper:hover,
  .dataset-card-wrapper.is-hovered {
    opacity: 1 !important;
    filter: none !important;
    transform: scale(1) !important;
    z-index: 10;
    position: relative;
  }
`

const DatasetCard = styled(Card)<{ $isSelected: boolean }>`
  background: linear-gradient(145deg, #1e293b 0%, #0f172a 100%);
  border: 2px solid
    ${(props) => (props.$isSelected ? 'rgba(102, 126, 234, 0.8)' : 'rgba(102, 126, 234, 0.3)')};
  border-radius: 12px;
  transition: all 0.3s ease;
  cursor: pointer;
  overflow: hidden;
  position: relative;
  box-shadow: 0 8px 20px rgba(0, 0, 0, 0.4), 0 0 0 1px rgba(255, 255, 255, 0.05),
    inset 0 1px 0 rgba(255, 255, 255, 0.1);
  backdrop-filter: blur(10px);
  height: 100%;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 3px;
    background: ${(props) =>
      props.$isSelected
        ? 'linear-gradient(90deg, #667eea 0%, #764ba2 100%)'
        : 'linear-gradient(90deg, rgba(102, 126, 234, 0.3) 0%, rgba(118, 75, 162, 0.3) 100%)'};
    opacity: ${(props) => (props.$isSelected ? 1 : 0.5)};
    transition: opacity 0.3s ease;
  }

  &:hover {
    transform: translateY(-3px);
    box-shadow: 0 12px 30px rgba(0, 0, 0, 0.5), 0 0 0 1px rgba(255, 255, 255, 0.1),
      inset 0 1px 0 rgba(255, 255, 255, 0.15);
    border-color: ${(props) =>
      props.$isSelected ? 'rgba(102, 126, 234, 1)' : 'rgba(102, 126, 234, 0.5)'};
    background: linear-gradient(145deg, #1e293b 0%, #0f172a 100%);

    &::before {
      opacity: 1;
    }
  }

  .card-body {
    padding: 1.5rem;
    background: transparent;
  }

  .selected-badge {
    position: absolute;
    top: 0.75rem;
    right: 0.75rem;
    width: 36px;
    height: 36px;
    border-radius: 50%;
    background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
    display: flex;
    align-items: center;
    justify-content: center;
    box-shadow: 0 4px 12px rgba(16, 185, 129, 0.6), 0 0 0 3px rgba(16, 185, 129, 0.2),
      0 0 20px rgba(16, 185, 129, 0.4);
    border: 3px solid rgba(16, 185, 129, 1);
    z-index: 2;
    animation: pulse 2s ease-in-out infinite;

    @keyframes pulse {
      0%,
      100% {
        box-shadow: 0 4px 12px rgba(16, 185, 129, 0.6), 0 0 0 3px rgba(16, 185, 129, 0.2),
          0 0 20px rgba(16, 185, 129, 0.4);
      }
      50% {
        box-shadow: 0 4px 16px rgba(16, 185, 129, 0.8), 0 0 0 4px rgba(16, 185, 129, 0.3),
          0 0 30px rgba(16, 185, 129, 0.6);
      }
    }

    svg {
      color: #10b981;
      filter: drop-shadow(0 1px 2px rgba(0, 0, 0, 0.3));
    }
  }
`

const CardHeader = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  margin-bottom: 1rem;
  padding-bottom: 1rem;
  border-bottom: 1px solid rgba(255, 255, 255, 0.15);
`

const CardTitle = styled.div`
  flex: 1;

  .dataset-name {
    font-size: 1.5rem;
    font-weight: 700;
    background: linear-gradient(135deg, #ffffff 0%, rgba(102, 126, 234, 0.9) 100%);
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    background-clip: text;
    margin-bottom: 0.5rem;
    line-height: 1.3;
    text-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
  }

  .dataset-source {
    font-size: 0.85rem;
    color: rgba(255, 255, 255, 0.6);
    font-weight: 500;
  }
`

const TypeBadge = styled(Badge)<{ $type: 'training' | 'validation' }>`
  background: ${(props) =>
    props.$type === 'training'
      ? 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)'
      : 'linear-gradient(135deg, #10b981 0%, #059669 100%)'};
  color: white;
  font-weight: 600;
  padding: 0.4rem 0.9rem;
  border-radius: 8px;
  font-size: 0.75rem;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  box-shadow: ${(props) =>
    props.$type === 'training'
      ? '0 2px 8px rgba(102, 126, 234, 0.4)'
      : '0 2px 8px rgba(16, 185, 129, 0.4)'};
  border: 1px solid
    ${(props) =>
      props.$type === 'training' ? 'rgba(102, 126, 234, 0.3)' : 'rgba(16, 185, 129, 0.3)'};
`

const ThumbnailStack = styled.div`
  position: relative;
  width: 100%;
  height: 100px;
  margin-bottom: 0.5rem;
  margin-top: 0.5rem;
  display: flex;
  align-items: center;
  justify-content: center;

  .thumbnail-wrapper {
    position: relative;
    width: 100%;
    height: 100%;
    display: flex;
    align-items: center;
    justify-content: center;
  }

  .thumbnail {
    position: absolute;
    width: 70px;
    height: 70px;
    border-radius: 8px;
    border: 2px solid rgba(102, 126, 234, 0.3);
    overflow: hidden;
    background: rgba(0, 0, 0, 0.3);
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.3);
    transition: all 0.3s ease;
    cursor: pointer;

    img {
      width: 100%;
      height: 100%;
      object-fit: cover;
    }

    &:nth-child(1) {
      left: calc(50% - 140px);
      z-index: 5;
      transform: rotate(-8deg) translateY(0);
    }

    &:nth-child(2) {
      left: calc(50% - 85px);
      z-index: 4;
      transform: rotate(-4deg) translateY(0);
    }

    &:nth-child(3) {
      left: calc(50% - 35px);
      z-index: 3;
      transform: rotate(0deg) translateY(0);
    }

    &:nth-child(4) {
      left: calc(50% + 15px);
      z-index: 2;
      transform: rotate(4deg) translateY(0);
    }

    &:nth-child(5) {
      left: calc(50% + 65px);
      z-index: 1;
      transform: rotate(8deg) translateY(0);
      opacity: 0.8;
    }

    &:hover {
      z-index: 10 !important;
      transform: scale(2) rotate(0deg) translateY(-5px) !important;
      box-shadow: 0 8px 24px rgba(102, 126, 234, 0.8);
      border-color: rgba(102, 126, 234, 0.9);
    }
  }

  .no-thumbnails {
    width: 100%;
    height: 100%;
    display: flex;
    align-items: center;
    justify-content: center;
    background: rgba(0, 0, 0, 0.2);
    border-radius: 8px;
    border: 1px dashed rgba(102, 126, 234, 0.3);
    color: rgba(255, 255, 255, 0.4);
    font-size: 0.75rem;
  }
`

const StatsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 1rem;
  margin-top: 1rem;
`

const StatItem = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 0.75rem;
  background: rgba(0, 0, 0, 0.4);
  border-radius: 8px;
  border: 1px solid rgba(102, 126, 234, 0.2);
  backdrop-filter: blur(5px);

  .stat-icon {
    width: 36px;
    height: 36px;
    border-radius: 8px;
    background: linear-gradient(135deg, rgba(102, 126, 234, 0.3) 0%, rgba(118, 75, 162, 0.3) 100%);
    display: flex;
    align-items: center;
    justify-content: center;
    color: rgba(102, 126, 234, 1);
    flex-shrink: 0;
    border: 1px solid rgba(102, 126, 234, 0.2);
  }

  .stat-content {
    flex: 1;
    min-width: 0;
  }

  .stat-label {
    font-size: 0.75rem;
    color: rgba(255, 255, 255, 0.6);
    text-transform: uppercase;
    letter-spacing: 0.5px;
    margin-bottom: 0.25rem;
    font-weight: 500;
  }

  .stat-value {
    font-size: 1rem;
    font-weight: 600;
    color: #fff;
  }
`

const EmptyState = styled.div`
  text-align: center;
  padding: 4rem 2rem;
  color: rgba(255, 255, 255, 0.5);

  .empty-icon {
    width: 64px;
    height: 64px;
    margin: 0 auto 1.5rem;
    opacity: 0.3;
    color: rgba(255, 255, 255, 0.3);
  }

  .empty-title {
    font-size: 1.25rem;
    font-weight: 600;
    margin-bottom: 0.5rem;
    color: rgba(255, 255, 255, 0.8);
  }

  .empty-subtitle {
    font-size: 0.95rem;
  }
`

const Footer = styled.div`
  padding: 1.5rem 2rem;
  border-top: 1px solid rgba(255, 255, 255, 0.1);
  display: flex;
  justify-content: space-between;
  align-items: center;
  background: rgba(0, 0, 0, 0.2);

  .info-text {
    color: rgba(255, 255, 255, 0.6);
    font-size: 0.9rem;
    display: flex;
    align-items: center;
    gap: 0.5rem;

    svg {
      color: rgba(102, 126, 234, 1);
    }
  }

  .action-buttons {
    display: flex;
    gap: 0.75rem;
  }
`

// Narrow React-Bootstrap Button's very large union props to simplify TS types
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const ButtonBase = Button as unknown as React.ComponentType<any>

const StyledButton = styled(ButtonBase)`
  padding: 0.6rem 1.5rem;
  border-radius: 10px;
  font-weight: 500;
  transition: all 0.2s;

  &.btn-outline-secondary {
    background: transparent;
    border: 1px solid rgba(255, 255, 255, 0.2);
    color: rgba(255, 255, 255, 0.7);

    &:hover {
      background: rgba(255, 255, 255, 0.1);
      border-color: rgba(255, 255, 255, 0.3);
      color: #fff;
    }
  }

  &.btn-primary {
    background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
    border: none;
    color: #fff;

    &:hover:not(:disabled) {
      background: linear-gradient(135deg, #764ba2 0%, #667eea 100%);
      transform: translateY(-1px);
      box-shadow: 0 4px 15px rgba(102, 126, 234, 0.3);
    }

    &:disabled {
      opacity: 0.5;
      cursor: not-allowed;
    }
  }
`

interface ThumbnailImageProps {
  contentId: string
  projectId?: string
  datasetId?: string
}

const ThumbnailImage: React.FC<ThumbnailImageProps> = ({ contentId, projectId, datasetId }) => {
  const { data: imageUrl } = useGetContentImageQuery(
    {
      ContentID: contentId,
      project_id: projectId || '',
      dataset_id: datasetId,
    },
    { skip: !projectId || !contentId }
  )

  if (!imageUrl) {
    return null
  }

  return (
    <div className="thumbnail">
      <img src={imageUrl} alt="Dataset thumbnail" loading="lazy" />
    </div>
  )
}

interface DatasetCardProps {
  name: string
  datasetSource: string
  isSelected: boolean
  onClick: () => void
  stats?: DatasetStats
  singleSelect: boolean
  projectId?: string
  datasetId?: string
}

const DatasetCardComponent: React.FC<DatasetCardProps> = ({
  name,
  datasetSource,
  isSelected,
  onClick,
  stats,
  projectId,
  datasetId,
}) => {
  const isTraining = datasetSource.endsWith(':t')
  const totalImages = (stats?.total_unannotated_images || 0) + (stats?.total_annotated_images || 0)
  const classCount = Object.keys(stats?.annotations_per_class || {}).length
  const annotationCount = stats?.total_annotations || 0
  const completeness =
    totalImages > 0 ? Math.round(((stats?.total_annotated_images || 0) / totalImages) * 100) : 0

  // Fetch sample images for thumbnails
  const { data: sampleData } = useSampleContentQuery(
    {
      project_id: projectId || '',
      count: 5,
      source: [datasetSource],
    },
    { skip: !projectId || totalImages === 0 }
  )

  const sampleImages = sampleData?.content || []

  return (
    <DatasetCard $isSelected={isSelected} onClick={onClick}>
      {isSelected && (
        <div className="selected-badge">
          <Check size={16} />
        </div>
      )}

      <Card.Body>
        <CardHeader>
          <CardTitle>
            <div className="dataset-name">{name}</div>
            <div className="dataset-source">{datasetSource}</div>
          </CardTitle>
          <TypeBadge $type={isTraining ? 'training' : 'validation'}>
            {isTraining ? 'Training' : 'Validation'}
          </TypeBadge>
        </CardHeader>

        {totalImages > 0 && (
          <ThumbnailStack>
            {sampleImages.length > 0 ? (
              <div className="thumbnail-wrapper">
                {sampleImages.slice(0, 5).map((content) => (
                  <ThumbnailImage
                    key={content.id}
                    contentId={content.id}
                    projectId={projectId}
                    datasetId={datasetId}
                  />
                ))}
              </div>
            ) : (
              <div className="no-thumbnails">
                <Image size={20} />
              </div>
            )}
          </ThumbnailStack>
        )}

        <StatsGrid>
          <StatItem>
            <div className="stat-icon">
              <Tag size={18} />
            </div>
            <div className="stat-content">
              <div className="stat-label">Classes</div>
              <div className="stat-value">{classCount}</div>
            </div>
          </StatItem>

          <StatItem>
            <div className="stat-icon">
              <Image size={18} />
            </div>
            <div className="stat-content">
              <div className="stat-label">Images</div>
              <div className="stat-value">{totalImages.toLocaleString()}</div>
            </div>
          </StatItem>

          <StatItem>
            <div className="stat-icon">
              <Layers size={18} />
            </div>
            <div className="stat-content">
              <div className="stat-label">Annotations</div>
              <div className="stat-value">{annotationCount.toLocaleString()}</div>
            </div>
          </StatItem>

          <StatItem>
            <div className="stat-icon">
              <BarChart3 size={18} />
            </div>
            <div className="stat-content">
              <div className="stat-label">Complete</div>
              <div className="stat-value">{completeness}%</div>
            </div>
          </StatItem>
        </StatsGrid>
      </Card.Body>
    </DatasetCard>
  )
}

interface SelectDatasetModalProps {
  show?: boolean
  singleSelect?: boolean
  canCreate?: boolean
  onHide: () => void
  handleAcceptSelections: (sources: string[]) => void
  selectedSources?: string[]
  updateGlobalState?: boolean
}

const SelectDatasetModal: React.FC<SelectDatasetModalProps> = ({
  show = false,
  singleSelect = false,
  canCreate = true,
  onHide,
  handleAcceptSelections,
  selectedSources = [],
  updateGlobalState = true,
}) => {
  const dispatch = useAppDispatch()
  const selectedProjectId = useAppSelector(selectSelectedProjectId)
  const [selectedDatasets, setSelectedDatasets] = useState<string[]>([])

  const { data: metadata } = useGetContentMetadataQuery(
    {
      project_id: selectedProjectId || '',
    },
    { skip: !selectedProjectId }
  )

  const { data: project } = useGetProjectQuery(selectedProjectId || '', {
    skip: !selectedProjectId,
  })

  const availableDatasets = useMemo(() => metadata?.metadata?.datasource || [], [metadata])
  const datasetStats = useDatasetStats(
    selectedProjectId || '',
    project?.datasetid || '',
    availableDatasets
  )

  const [searchTerm, setSearchTerm] = useState('')
  const [purposeFilter, setPurposeFilter] = useState('all')
  const [viewMode, setViewMode] = useState<'grid' | 'list'>('grid')
  const [sortBy, setSortBy] = useState('name')
  const [hoveredCardId, setHoveredCardId] = useState<string | null>(null)
  const prevSelectedSourcesRef = useRef<string[]>([])

  const filteredAndSortedDatasets = useMemo(() => {
    const filtered = availableDatasets.filter((dataset) => {
      const matchesSearch = dataset.toLowerCase().includes(searchTerm.toLowerCase())
      if (purposeFilter === 'all') return matchesSearch
      const matchesPurpose = dataset.endsWith(purposeFilter.toLowerCase().slice(0, 1))
      return matchesSearch && matchesPurpose
    })

    filtered.sort((a, b) => {
      switch (sortBy) {
        case 'name':
          return a.split(':')[0].localeCompare(b.split(':')[0])
        case 'type':
          return a.endsWith(':t') === b.endsWith(':t') ? 0 : a.endsWith(':t') ? -1 : 1
        case 'images':
          const aImages =
            (datasetStats[a]?.total_unannotated_images || 0) +
            (datasetStats[a]?.total_annotated_images || 0)
          const bImages =
            (datasetStats[b]?.total_unannotated_images || 0) +
            (datasetStats[b]?.total_annotated_images || 0)
          return bImages - aImages
        case 'annotations':
          return (
            (datasetStats[b]?.total_annotations || 0) - (datasetStats[a]?.total_annotations || 0)
          )
        default:
          return 0
      }
    })

    return filtered
  }, [availableDatasets, purposeFilter, searchTerm, sortBy, datasetStats])

  useEffect(() => {
    if (selectedSources.length > 0 && !isEqual(selectedSources, prevSelectedSourcesRef.current)) {
      prevSelectedSourcesRef.current = selectedSources
    }
  }, [selectedSources])

  const handleToggleSelection = useCallback(
    (datasetSource: string) => {
      if (singleSelect) {
        setSelectedDatasets([datasetSource])
        return
      }

      const isSelected = selectedDatasets.includes(datasetSource)
      if (isSelected) {
        setSelectedDatasets(selectedDatasets.filter((ds) => ds !== datasetSource))
      } else {
        setSelectedDatasets([...selectedDatasets, datasetSource])
      }
    },
    [singleSelect, selectedDatasets]
  )

  const handleClearSelection = useCallback(() => {
    setSelectedDatasets([])
  }, [])

  const handleSelectAll = useCallback(() => {
    if (!singleSelect) {
      setSelectedDatasets(filteredAndSortedDatasets)
    }
  }, [singleSelect, filteredAndSortedDatasets])

  const handleAccept = useCallback(() => {
    handleAcceptSelections(selectedDatasets)
    onHide()
  }, [handleAcceptSelections, selectedDatasets, onHide])

  return (
    <StyledModal size="xl" show={show} onHide={onHide} centered>
      <Modal.Header closeButton>
        <Modal.Title>
          <Package size={20} />
          Dataset Catalog
        </Modal.Title>
      </Modal.Header>

      <Modal.Body>
        <TopBar>
          <SearchWrapper>
            <Search size={18} className="search-icon" />
            <Form.Control
              type="text"
              placeholder="Search datasets..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </SearchWrapper>

          <FilterGroup>
            <FilterButton
              $active={purposeFilter === 'all'}
              onClick={() => setPurposeFilter('all')}
              type="button"
            >
              <Filter size={14} />
              All
            </FilterButton>
            <FilterButton
              $active={purposeFilter === 'training'}
              onClick={() => setPurposeFilter('training')}
              type="button"
            >
              Training
            </FilterButton>
            <FilterButton
              $active={purposeFilter === 'validation'}
              onClick={() => setPurposeFilter('validation')}
              type="button"
            >
              Validation
            </FilterButton>

            <SortDropdown>
              <Dropdown.Toggle variant="outline-secondary" className="dropdown-toggle">
                <ArrowUpDown size={14} />
                Sort: {sortBy.charAt(0).toUpperCase() + sortBy.slice(1)}
              </Dropdown.Toggle>
              <Dropdown.Menu>
                <Dropdown.Item active={sortBy === 'name'} onClick={() => setSortBy('name')}>
                  Name
                </Dropdown.Item>
                <Dropdown.Item active={sortBy === 'type'} onClick={() => setSortBy('type')}>
                  Type
                </Dropdown.Item>
                <Dropdown.Item active={sortBy === 'images'} onClick={() => setSortBy('images')}>
                  Image Count
                </Dropdown.Item>
                <Dropdown.Item
                  active={sortBy === 'annotations'}
                  onClick={() => setSortBy('annotations')}
                >
                  Annotations
                </Dropdown.Item>
              </Dropdown.Menu>
            </SortDropdown>

            <ViewToggleGroup>
              <ViewToggleButton
                $active={viewMode === 'grid'}
                onClick={() => setViewMode('grid')}
                type="button"
              >
                <Grid size={16} />
              </ViewToggleButton>
              <ViewToggleButton
                $active={viewMode === 'list'}
                onClick={() => setViewMode('list')}
                type="button"
              >
                <List size={16} />
              </ViewToggleButton>
            </ViewToggleGroup>
          </FilterGroup>
        </TopBar>

        <ContentArea>
          {selectedDatasets.length > 0 && (
            <SelectionBanner>
              <div className="selection-info">
                <CheckCircle2 size={18} />
                <span>
                  <strong>{selectedDatasets.length}</strong> dataset
                  {selectedDatasets.length !== 1 ? 's' : ''} selected
                </span>
              </div>
              <button className="clear-btn" onClick={handleClearSelection} type="button">
                <X size={14} style={{ marginRight: '0.25rem' }} />
                Clear
              </button>
            </SelectionBanner>
          )}

          <ResultsHeader>
            <div className="results-count">
              Showing <strong>{filteredAndSortedDatasets.length}</strong> of{' '}
              <strong>{availableDatasets.length}</strong> datasets
            </div>
            {!singleSelect && (
              <button
                className="select-all-btn"
                onClick={handleSelectAll}
                disabled={
                  filteredAndSortedDatasets.length === 0 ||
                  filteredAndSortedDatasets.every((d) => selectedDatasets.includes(d))
                }
                type="button"
              >
                Select All
              </button>
            )}
          </ResultsHeader>

          {filteredAndSortedDatasets.length > 0 ? (
            <CardsContainer $hasHover={hoveredCardId !== null}>
              <Row
                xs={1}
                md={viewMode === 'grid' ? 2 : 1}
                lg={viewMode === 'grid' ? 3 : 1}
                className="g-3"
              >
                {filteredAndSortedDatasets.map((dataset) => {
                  const name = dataset.split(':')[0]
                  const isHovered = hoveredCardId === dataset

                  return (
                    <Col key={dataset}>
                      <div
                        className={`dataset-card-wrapper ${isHovered ? 'is-hovered' : ''}`}
                        onMouseEnter={() => setHoveredCardId(dataset)}
                        onMouseLeave={() => setHoveredCardId(null)}
                      >
                        <DatasetCardComponent
                          name={name}
                          datasetSource={dataset}
                          isSelected={selectedDatasets.includes(dataset)}
                          onClick={() => handleToggleSelection(dataset)}
                          stats={datasetStats[dataset]}
                          singleSelect={singleSelect}
                          projectId={selectedProjectId || undefined}
                          datasetId={project?.datasetid || undefined}
                        />
                      </div>
                    </Col>
                  )
                })}
              </Row>
            </CardsContainer>
          ) : (
            <EmptyState>
              <Database size={64} className="empty-icon" />
              <h3 className="empty-title">No datasets found</h3>
              <p className="empty-subtitle">
                Try adjusting your search or filter criteria to find datasets.
              </p>
            </EmptyState>
          )}
        </ContentArea>

        <Footer>
          {selectedDatasets.length === 0 && !singleSelect && (
            <div className="info-text">
              <Database size={16} />
              <span>No datasets selected - all available data will be shown</span>
            </div>
          )}
          {selectedDatasets.length > 0 && <div />}

          <div className="action-buttons">
            <StyledButton variant="outline-secondary" onClick={onHide}>
              Cancel
            </StyledButton>
            <StyledButton
              variant="primary"
              onClick={handleAccept}
              disabled={singleSelect && selectedDatasets.length === 0}
            >
              {singleSelect ? 'Select Dataset' : 'Apply Selection'}
            </StyledButton>
          </div>
        </Footer>
      </Modal.Body>
    </StyledModal>
  )
}

export default SelectDatasetModal
